import { cx } from '../atoms/cx.ts'
import { hasSkin } from '../tokens/skin.ts'

import { AssemblerLine, ShakerLine } from './LineFigures.tsx'

import './figures.css'

/**
 * 모듈 본체 그림 — 와이어프레임 `moduleFig()` 1215행.
 *
 * 🔴 어셈블러·염색기 본체는 **평면도(위에서 본 모습)** 로 통일한다(§16-5).
 *    틸트 각도 같은 세부 수치는 그리지 않는다.
 */

/** 어셈블러 평면도 — 베이스 · 프레임 기둥 2 · UV 램프 하우징 · 틸트 스테이지 · 구동부 (1232행) */
export function AssemblerFigure({
  uvOn,
  chamberSlot,
  uvLabel,
  label,
}: {
  uvOn: boolean
  /** 지금 스테이지에 올라간 챔버 칸 이름. 없으면 빈 스테이지 */
  chamberSlot?: string
  /** 「UV on」/「UV off」 표기 */
  uvLabel: string
  label: string
}) {
  /* 통합 콘솔은 디자인팀 시안의 선 도면(`LineFigures.tsx`) — 스킨 없는 앱은 아래 종전 그림 그대로다 */
  if (hasSkin()) {
    return <AssemblerLine chamberSlot={chamberSlot} label={label} uvLabel={uvLabel} uvOn={uvOn} />
  }
  return (
    <svg
      aria-label={label}
      className="fig fig-mod fig-mod--asm"
      height={150}
      role="img"
      strokeLinejoin="round"
      strokeWidth={3}
      viewBox="0 0 160 150"
      width={160}
    >
      <rect height={118} rx={6} width={144} x={8} y={12} />
      <rect className="fig-mod__frame" height={106} rx={2} width={10} x={14} y={18} />
      <rect className="fig-mod__frame" height={106} rx={2} width={10} x={136} y={18} />
      <rect
        className={cx(uvOn ? 'fig-mod__solid' : 'fig-mod__dashed')}
        height={14}
        rx={3}
        width={100}
        x={30}
        y={20}
      />
      {uvOn ? (
        <path
          className="fig-mod__hair"
          d="M45 38v10M62 38v10M80 38v10M98 38v10M115 38v10"
          strokeDasharray="2 2"
        />
      ) : null}
      <rect
        className={cx(!uvOn && 'fig-mod__dashed')}
        height={54}
        rx={4}
        strokeWidth={2}
        width={76}
        x={42}
        y={56}
      />
      <line className="fig-mod__hair" opacity={0.45} x1={42} x2={118} y1={83} y2={83} />
      {chamberSlot === undefined ? null : (
        <>
          <rect className="fig-mod__solid" height={22} rx={3} width={32} x={64} y={72} />
          <rect className="fig-mod__plate" height={16} rx={2} width={26} x={67} y={75} />
          <text className="fig-mod__txt" textAnchor="middle" x={80} y={86.5}>
            {chamberSlot}
          </text>
        </>
      )}
      <rect className="fig-mod__solid" height={46} rx={2} width={12} x={120} y={60} />
      <circle className="fig-mod__hollow" cx={30} cy={118} r={4} />
      <circle className="fig-mod__hollow" cx={130} cy={118} r={4} />
      <text className="fig-mod__txt" data-part="status" textAnchor="middle" x={80} y={145}>
        {uvLabel}
      </text>
    </svg>
  )
}

/** TC 랙 — 3×3 슬라이드 자리. 남은 칸은 채움, 쓴 칸은 점선 (1236행) */
export function TcRackFigure({ remaining, label }: { remaining: number; label: string }) {
  return (
    <svg
      aria-label={label}
      className="fig fig-mod fig-mod--tc"
      height={62}
      role="img"
      viewBox="0 0 160 62"
      width={160}
    >
      <rect height={54} rx={5} strokeWidth={2} width={152} x={4} y={4} />
      {Array.from({ length: 9 }, (_unused, n) => {
        const row = Math.floor(n / 3)
        const col = n % 3
        const left = n >= 9 - remaining
        return (
          <rect
            className={left ? 'fig-mod__solid' : 'fig-mod__well'}
            height={11}
            key={n}
            rx={2}
            width={36}
            x={14 + col * 48}
            y={10 + row * 16}
          />
        )
      })}
    </svg>
  )
}

/** 슬라이더 정지 자리 — capability `tray_transport.positions` 어휘 그대로(`work` = 피펫팅 자리) */
export type ShakerStop = 'rear' | 'work' | 'front'

/**
 * 걸쇠 모양 — `shaker_latch.state` 6상태를 **모양 넷**으로 가른다.
 * `open`(idle_open) · `closed`(idle_closed) · `moving`(opening · closing).
 * 🔴 `idle_unknown` · `unknown` · 값 없음은 `null` — 턱을 안 그린다. 「닫힘」으로 접지 않는다(`FR-LH-19`).
 */
export type ShakerLatchShape = 'open' | 'closed' | 'moving'

export interface ShakerFigureState {
  /** 플랫폼이 선 자리. `null` = 모름 → 세 자리 어디에도 세우지 않는다 */
  stop: ShakerStop | null
  latch: ShakerLatchShape | null
  /** `tray_latch` — `present`(engaged) · `absent`(released) · `null`(모름) */
  tray: 'present' | 'absent' | null
  /** 모듈 상태 `SHAKING` */
  shaking: boolean
}

/** 정지 자리 세로 중심 — 위가 후면(6축 쪽), 아래가 전면(사람 쪽) */
const SHAKER_STOP_Y: Readonly<Record<ShakerStop, number>> = { rear: 40, work: 94, front: 148 }
const SHAKER_STOPS: readonly ShakerStop[] = ['rear', 'work', 'front']

/**
 * 염색기 셰이커 — **평면도(위에서 본 모습)**. 본체 안에 세로 레일 둘, 그 위를 오가는 셰이커 플랫폼.
 * 어셈블러 · 원심분리기 그림과 한 가족이다: 굵은 먹색 둥근 본체 · 부품은 중간 굵기 · 점선은 「없음/비어 있음」뿐.
 *
 * | 값 | 그림 |
 * | --- | --- |
 * | 자리 | 플랫폼이 그 정지 자리에 선다 · 레일 옆 눈금이 굵어지고 글자가 진해진다 |
 * | 자리 모름 | 플랫폼 대신 **이동 범위 전체의 점선 윤곽** — 어느 자리에도 확정하지 않는다. 걸쇠·트레이도 안 그린다 |
 * | 래치 | 플랫폼 좌우의 걸쇠: 닫힘 = 턱이 안쪽으로 트레이를 누른다 · 열림 = 턱이 세로로 비켜 섰다 · 움직이는 중 = 턱이 비스듬 · 모름 = 축만 |
 * | 트레이 | 결착 = 3×3 칸 윤곽 · 풀림 = 트레이 자리 점선(비어 있음) · 모름 = 없음 |
 * | 쉐이킹 | 플랫폼 테가 강조색 + 양옆 진동선(`--dur-pulse` 로 깜빡 · 움직임 줄이기면 0ms) |
 *
 * 🔴 **L 트레이를 따로 또 그리지 않는다.** 염색기 안에서 트레이는 이 판에 결착된 한 장이다(§16-2).
 * 🔴 **색만으로 가르지 않는다** — 자리는 위치·눈금 굵기·글자 굵기, 래치는 턱 모양, 쉐이킹은 진동선이 말한다.
 * ⚠ 바깥 `viewBox`(116 × 200)는 그대로다 — 장비 탭의 맞춤 배율(`--fig-fit-n: 200`)이 이 높이를 센다.
 */
export function ShakerFigure({
  stop,
  latch,
  tray,
  shaking,
  stopLabels,
  statusLabel,
  label,
  door = null,
  doorLabel,
}: ShakerFigureState & {
  stopLabels: Readonly<Record<ShakerStop, string>>
  /** 아래 한 줄(어셈블러 「UV off」 자리) */
  statusLabel: string
  label: string
  /** 후문(자동) 상태 · 글자 — 콘솔 선 도면만 그린다(2026-10-07). 스킨 없는 그림은 종전 그대로 */
  door?: 'open' | 'closed' | null
  doorLabel?: string | undefined
}) {
  /* 통합 콘솔은 디자인팀 시안의 선 도면(`LineFigures.tsx`) — 스킨 없는 앱은 아래 종전 그림 그대로다 */
  if (hasSkin()) {
    return (
      <ShakerLine
        door={door}
        doorLabel={doorLabel}
        label={label}
        latch={latch}
        shaking={shaking}
        statusLabel={statusLabel}
        stop={stop}
        stopLabels={stopLabels}
        tray={tray}
      />
    )
  }
  const cy = stop === null ? null : SHAKER_STOP_Y[stop]

  return (
    <svg
      aria-label={label}
      className={cx('fig fig-mod fig-mod--shaker', shaking && 'fig-mod--shaking')}
      data-latch={latch ?? 'unknown'}
      data-shaking={shaking ? 'true' : 'false'}
      data-stop={stop ?? 'unknown'}
      data-tray={tray ?? 'unknown'}
      height={200}
      role="img"
      strokeLinejoin="round"
      strokeWidth={3}
      viewBox="0 0 116 200"
      width={116}
    >
      <rect height={176} rx={6} width={104} x={6} y={6} />
      {/* 레일 둘 — 슬라이드 축 */}
      <rect className="fig-mod__frame" height={152} rx={2} width={4} x={19} y={18} />
      <rect className="fig-mod__frame" height={152} rx={2} width={4} x={65} y={18} />
      {SHAKER_STOPS.map((id) => {
        const y = SHAKER_STOP_Y[id]
        const on = id === stop
        return (
          <g data-stop-mark={id} key={id}>
            <line
              className={cx('fig-mod__tick', on && 'fig-mod__tick--on')}
              x1={78}
              x2={84}
              y1={y}
              y2={y}
            />
            <text
              className={cx('fig-mod__note', on && 'fig-mod__note--on')}
              textAnchor="middle"
              x={97}
              y={y + 3}
            >
              {stopLabels[id]}
            </text>
          </g>
        )
      })}
      {cy === null ? (
        <rect
          className="fig-mod__dashed"
          data-part="platform-unknown"
          height={152}
          rx={4}
          width={54}
          x={17}
          y={18}
        />
      ) : (
        <g data-part="platform" data-y={cy}>
          <rect className="fig-mod__platform" height={44} rx={4} width={54} x={17} y={cy - 22} />
          {tray === 'present' ? (
            <g data-part="tray">
              <rect className="fig-mod__plate" height={34} rx={2} width={38} x={25} y={cy - 17} />
              {Array.from({ length: 9 }, (_unused, n) => (
                <rect
                  className="fig-mod__cell"
                  height={8}
                  key={n}
                  rx={1}
                  width={10}
                  x={28 + (n % 3) * 11}
                  y={cy - 14 + Math.floor(n / 3) * 10}
                />
              ))}
            </g>
          ) : tray === 'absent' ? (
            <rect
              className="fig-mod__dashed"
              data-part="tray-empty"
              height={34}
              rx={2}
              width={38}
              x={25}
              y={cy - 17}
            />
          ) : null}
          <ShakerLatch cy={cy} shape={latch} side="left" />
          <ShakerLatch cy={cy} shape={latch} side="right" />
          {shaking ? (
            <path
              className="fig-mod__wave"
              d={`M13 ${String(cy - 9)}q-4 9 0 18M75 ${String(cy - 9)}q4 9 0 18`}
              data-part="wave"
            />
          ) : null}
        </g>
      )}
      <text className="fig-mod__txt" data-part="status" textAnchor="middle" x={58} y={196}>
        {statusLabel}
      </text>
    </svg>
  )
}

/**
 * 걸쇠 하나 — 축(원) + 턱(굵은 짧은 선). 🔴 상태는 **턱의 방향**이 말한다.
 * 닫힘 = 트레이 쪽 수평 · 열림 = 테를 따라 세로 · 움직이는 중 = 비스듬 · 모름 = 턱 없음.
 */
function ShakerLatch({
  cy,
  shape,
  side,
}: {
  cy: number
  shape: ShakerLatchShape | null
  side: 'left' | 'right'
}) {
  const x = side === 'left' ? 21 : 67
  const inward = side === 'left' ? 1 : -1
  const jaw =
    shape === 'closed'
      ? `M${String(x)} ${String(cy)}h${String(6 * inward)}`
      : shape === 'open'
        ? `M${String(x)} ${String(cy)}v-8`
        : shape === 'moving'
          ? `M${String(x)} ${String(cy)}l${String(5 * inward)} -5`
          : null
  return (
    <g data-latch-side={side}>
      {jaw === null ? null : <path className="fig-mod__jaw" d={jaw} />}
      <circle className="fig-mod__hollow" cx={x} cy={cy} r={2} />
    </g>
  )
}

/**
 * 염색기 피펫·시약 데크 — 상온 랙 5(RA1~RA5) + 냉장 블록 3(RC1~RC3 · 4 ℃) + 팁 랙 2 + 폐기통 (1278행).
 *
 * 🔴 **자리가 곧 보관 온도다**(§16-3). 시약 채널 수는 고정이 아니고 물리 상한이 자리 8개다.
 * ⚠ 위 「팁 랙 2(6×4)」는 **와이어프레임 그림**이다. 실물 랙(팁 랙 · 튜브 랙)은 `DeckRackFigure` 가
 *   그린다(처음은 시료 탭 오른쪽 판 · eric 2026-09-26 — 그 판은 2026-09-28 에 걷혀 지금은 소모품 서랍). ⚠ 통합 콘솔 장비 탭은 2026-09-27 부터 그 랙을
 *   썼다가(`FocusFigure` 의 `realFigures`), 2026-10-02 에 그 칸째 걷혔다(소모품 판과 겹침 — 랙 그림은 소모품 판 한 곳). 이 그림(과
 *   `TcRackFigure`)은 **장비 관점 콘솔 · 태블릿 · 모듈 패드** 길에서만 선다 — 그 앱들은 요청 전까지 그대로다.
 * ⚠ 이 그림에서 조각을 꺼내던 `part` · `wellNames` · `blankWells` 프롭은 **걷었다**(2026-09-26) —
 *   쓰던 곳이 시료 탭의 RA1 · RA2 · RC 베이였고, eric 이 그 베이를 뺐다. 되살리지 마라.
 */
export function ReagentBayFigure({
  activeWell,
  tipsUsed,
  activeRack,
  ambientCaption,
  chilledCaption,
  tipCaptions,
  wasteLabel,
  label,
}: {
  /** 지금 흡인·분주 중인 자리(`RA4`). 없으면 강조 없음 */
  activeWell?: string
  /** 팁 랙 1 에서 쓴 팁 수 */
  tipsUsed: number
  activeRack?: 1 | 2
  ambientCaption: string
  chilledCaption: string
  tipCaptions: readonly [string, string]
  wasteLabel: string
  label: string
}) {
  const well = (x: number, y: number, name: string) => {
    const on = name === activeWell
    return (
      <g key={name}>
        <rect
          className={cx('fig-mod__well', on && 'fig-mod__well--on')}
          height={15}
          rx={2}
          width={24}
          x={x}
          y={y}
        />
        <text
          className={cx('fig-mod__welltxt', on && 'fig-mod__welltxt--on')}
          textAnchor="middle"
          x={x + 12}
          y={y + 10.5}
        >
          {name}
        </text>
      </g>
    )
  }

  const tips = (x: number, y: number, used: number) =>
    Array.from({ length: 24 }, (_unused, n) => {
      const row = Math.floor(n / 6)
      const col = n % 6
      return (
        <circle
          className={n < used ? 'fig-mod__tip fig-mod__tip--used' : 'fig-mod__tip'}
          cx={x + 7 + col * 6}
          cy={y + 7 + row * 6.5}
          key={n}
          r={1.4}
        />
      )
    })

  return (
    <svg
      aria-label={label}
      className="fig fig-mod fig-mod--bay"
      height={200}
      role="img"
      strokeLinejoin="round"
      strokeWidth={3}
      viewBox="0 0 106 200"
      width={106}
    >
      <rect height={188} rx={6} width={94} x={6} y={6} />
      <rect height={42} rx={3} strokeWidth={1.5} width={82} x={12} y={14} />
      {['RA1', 'RA2', 'RA3'].map((name, i) => well(16 + i * 26, 18, name))}
      {['RA4', 'RA5'].map((name, i) => well(16 + i * 26, 36, name))}
      <text className="fig-mod__cap" x={16} y={64}>
        {ambientCaption}
      </text>
      <rect height={24} rx={3} strokeWidth={1.5} width={82} x={12} y={70} />
      {['RC1', 'RC2', 'RC3'].map((name, i) => well(16 + i * 26, 74, name))}
      <text className="fig-mod__cap" x={16} y={102}>
        {chilledCaption}
      </text>
      <rect height={32} rx={3} strokeWidth={activeRack === 1 ? 2 : 1.5} width={38} x={12} y={108} />
      {tips(12, 108, tipsUsed)}
      <text className="fig-mod__cap" x={16} y={148}>
        {tipCaptions[0]}
      </text>
      <rect height={32} rx={3} strokeWidth={activeRack === 2 ? 2 : 1.5} width={38} x={56} y={108} />
      {tips(56, 108, 0)}
      <text className="fig-mod__cap" x={60} y={148}>
        {tipCaptions[1]}
      </text>
      <rect height={24} rx={3} strokeDasharray="4 3" strokeWidth={1.5} width={82} x={12} y={156} />
      <text className="fig-mod__txt" textAnchor="middle" x={53} y={171}>
        {wasteLabel}
      </text>
    </svg>
  )
}
